/**
 * venueEventService.ts
 * Fetches upcoming venue events from Supabase.
 */

import { supabase } from '../lib/supabase'
import { todayLocalDateString, localDateStringOffset } from '../utils/dateUtils'

export interface VenueEvent {
  id: string
  venue_id: string
  title: string
  description: string
  event_date: string        // ISO date string YYYY-MM-DD
  start_time: string        // HH:MM
  end_time: string | null
  image_url: string | null
  ticket_url: string | null
  is_free: boolean
  price_text: string | null
  is_active: boolean
  is_featured: boolean
  created_at: string
  updated_at: string
  // Joined from venues
  venue_name?: string
  venue_neighborhood?: string
}

/**
 * Fetch upcoming events for the browse page.
 * Returns events from today onward, ordered by date then featured first.
 */
export async function getUpcomingEvents(limitDays = 30): Promise<VenueEvent[]> {
  // Local date, not UTC — toISOString() shifts the date forward in the
  // evening for US timezones, silently dropping tonight's events from
  // the "upcoming" results during peak usage hours.
  const today = todayLocalDateString()
  const cutoff = localDateStringOffset(limitDays)

  const { data, error } = await supabase
    .from('venue_events')
    .select(`
      *,
      venues (name, neighborhood)
    `)
    .eq('is_active', true)
    .gte('event_date', today)
    .lte('event_date', cutoff)
    .order('is_featured', { ascending: false })
    .order('event_date', { ascending: true })
    .limit(20)

  if (error) {
    console.error('getUpcomingEvents error:', error)
    return []
  }

  return (data ?? []).map((e: any) => ({
    ...e,
    venue_name: e.venues?.name ?? '',
    venue_neighborhood: e.venues?.neighborhood ?? '',
  }))
}

/**
 * Fetch events for a specific venue (used on detail page).
 */
export async function getVenueEvents(venueId: string): Promise<VenueEvent[]> {
  const today = todayLocalDateString()

  const { data, error } = await supabase
    .from('venue_events')
    .select('*')
    .eq('venue_id', venueId)
    .eq('is_active', true)
    .gte('event_date', today)
    .order('event_date', { ascending: true })
    .limit(10)

  if (error) {
    console.error('getVenueEvents error:', error)
    return []
  }

  return data ?? []
}

/**
 * Admin: create a new event.
 */
export async function createVenueEvent(
  event: Omit<VenueEvent, 'id' | 'created_at' | 'updated_at' | 'venue_name' | 'venue_neighborhood'>
): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from('venue_events')
    .insert([{ ...event }])
    .select('id')
    .single()

  if (error) { console.error('createVenueEvent error:', error); return null }
  return data
}

/**
 * Admin: update an event.
 */
export async function updateVenueEvent(
  id: string,
  updates: Partial<VenueEvent>
): Promise<boolean> {
  const { error } = await supabase
    .from('venue_events')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) { console.error('updateVenueEvent error:', error); return false }
  return true
}

/**
 * Admin: delete an event.
 */
export async function deleteVenueEvent(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('venue_events')
    .delete()
    .eq('id', id)

  if (error) { console.error('deleteVenueEvent error:', error); return false }
  return true
}
